// Hero canvas — variant 3: wireframe icosahedron + inner sphere.
import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment } from "@react-three/drei";
import * as THREE from "three";
import { useTheme } from "../../contexts/ThemeContext";

const h = React.createElement;

function WireIcosa({ mouse, isDark }) {
  const grp = useRef();
  useFrame((state) => {
    if (!grp.current) return;
    const t = state.clock.getElapsedTime();
    grp.current.rotation.x = THREE.MathUtils.lerp(
      grp.current.rotation.x,
      mouse.current.y * 0.5 + Math.sin(t * 0.15) * 0.15,
      0.05,
    );
    grp.current.rotation.y = THREE.MathUtils.lerp(
      grp.current.rotation.y,
      mouse.current.x * 0.6 + t * 0.1,
      0.05,
    );
  });

  const color = isDark ? "#ffffff" : "#0a0a0a";

  return h(
    Float,
    { speed: 1.0, rotationIntensity: 0.3, floatIntensity: 0.6 },
    h(
      "group",
      { ref: grp, scale: 2.4 },
      // Outer wireframe icosahedron
      h(
        "mesh",
        null,
        h("icosahedronGeometry", { args: [1, 2] }),
        h("meshBasicMaterial", { color, wireframe: true, transparent: true, opacity: 0.55 }),
      ),
      // Slightly larger ghost wireframe
      h(
        "mesh",
        { scale: 1.18, rotation: [0.4, 0.6, 0.2] },
        h("icosahedronGeometry", { args: [1, 1] }),
        h("meshBasicMaterial", { color, wireframe: true, transparent: true, opacity: 0.18 }),
      ),
      // Inner solid sphere — high metallic, gives the wireframe something to wrap
      h(
        "mesh",
        { scale: 0.55 },
        h("sphereGeometry", { args: [1, 64, 64] }),
        h("meshStandardMaterial", {
          color: isDark ? "#0a0a0a" : "#e4e4e7",
          metalness: 0.95,
          roughness: 0.18,
          envMapIntensity: 1.2,
        }),
      ),
    ),
  );
}

function OrbitDots({ isDark }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) ref.current.rotation.y = state.clock.getElapsedTime() * 0.15;
  });
  const positions = Array.from({ length: 80 }, (_, i) => {
    const a = (i / 80) * Math.PI * 2;
    const r = 4.2;
    return [Math.cos(a) * r, (Math.sin(a * 3) - 0.5) * 0.4, Math.sin(a) * r];
  });
  return h(
    "group",
    { ref },
    positions.map((p, i) =>
      h(
        "mesh",
        { key: i, position: p },
        h("sphereGeometry", { args: [0.025, 8, 8] }),
        h("meshBasicMaterial", { color: isDark ? "#ffffff" : "#27272a", transparent: true, opacity: 0.5 }),
      ),
    ),
  );
}

function Scene({ mouse, isDark }) {
  const bg = isDark ? "#050505" : "#fafafa";
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: [bg] }),
    h("fog", { attach: "fog", args: [bg, 6, 16] }),
    h("ambientLight", { intensity: isDark ? 0.25 : 0.6 }),
    h("directionalLight", { position: [5, 5, 4], intensity: 1.2 }),
    h(
      Suspense,
      { fallback: null },
      h(WireIcosa, { mouse, isDark }),
      h(OrbitDots, { isDark }),
      h(Environment, { preset: isDark ? "warehouse" : "studio" }),
    ),
  );
}

export default function HeroCanvasWireframe() {
  const mouse = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const onPointer = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    mouse.current.x = x;
    mouse.current.y = y;
  };

  return (
    <div
      className="absolute inset-0 -z-0"
      onPointerMove={onPointer}
      data-testid="hero-canvas-wireframe"
      aria-hidden="true"
    >
      <Canvas
        key={theme}
        dpr={[1, 1.6]}
        camera={{ position: [0, 0, 6.5], fov: 45 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Scene mouse={mouse} isDark={isDark} />
      </Canvas>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 35%, rgba(5,5,5,0.85) 85%)"
            : "radial-gradient(ellipse at center, transparent 35%, rgba(250,250,250,0.85) 85%)",
        }}
      />
    </div>
  );
}
